import {
  FaRunning,
  FaHeartbeat,
  FaFire,
  FaTachometerAlt,
  FaRoad,
  FaCalendarAlt,
} from "react-icons/fa";

function WorkRecordDetailView({ wr }) {
  if (!wr) return null;

  // 숫자는 소수점 둘째 자리까지
  const fmt = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    const num = Number(value);
    if (Number.isNaN(num)) return "-";

    // 0은 "-" 처리 (기존 정책)
    if (num === 0) return "-";

    // 소수점 2자리 반올림 → 문자열
    const fixed = num.toFixed(2);

    // 불필요한 0 제거 (23.80 → 23.8, 3.00 → 3)
    return fixed.replace(/\.?0+$/, "");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white px-4 py-6 pb-24">
      {/* 헤더 */}
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold mb-2 ${
            wr.courseName ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {wr.courseName || "코스 생성중..."}
        </h2>
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <FaCalendarAlt className="w-4 h-4" />
          <span className="text-sm font-medium">{wr.workRecordDate}</span>
        </div>
      </div>

      {/* 메인 통계 카드 */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {/* 총 거리 */}
        <div className="bg-white rounded-3xl p-4 shadow-lg border-2 border-gray-100 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
            style={{ backgroundColor: "rgba(0, 224, 198, 0.1)" }}
          >
            <FaRoad className="text-xl" style={{ color: "rgb(0, 224, 198)" }} />
          </div>
          <p className="text-2xl font-black text-gray-900 mb-1">
            {fmt(wr.distance)}
          </p>
          <p className="text-xs text-gray-500 font-semibold">거리 (km)</p>
        </div>

        {/* 평균 속도 */}
        <div className="bg-white rounded-3xl p-4 shadow-lg border-2 border-gray-100 text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 mx-auto mb-2 flex items-center justify-center">
            <FaTachometerAlt className="text-xl text-blue-500" />
          </div>
          <p className="text-2xl font-black text-gray-900 mb-1">
            {fmt(wr.avgSpeed)}
          </p>
          <p className="text-xs text-gray-500 font-semibold">속도 (km/h)</p>
        </div>

        {/* 칼로리 */}
        <div className="bg-white rounded-3xl p-4 shadow-lg border-2 border-gray-100 text-center">
          <div className="w-12 h-12 rounded-full bg-orange-50 mx-auto mb-2 flex items-center justify-center">
            <FaFire className="text-xl text-orange-500" />
          </div>
          <p className="text-2xl font-black text-gray-900 mb-1">
            {fmt(wr.calorie)}
          </p>
          <p className="text-xs text-gray-500 font-semibold">칼로리 (kcal)</p>
        </div>
      </div>

      {/* 세부 정보 */}
      <div className="bg-white rounded-3xl shadow-lg border-2 border-gray-100 p-5 space-y-1">
        <h3 className="text-base font-bold text-gray-800 mb-4">상세 기록</h3>

        {/* 최대 속도 */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <FaRunning className="text-blue-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">
              최대 속도
            </span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {fmt(wr.maxSpeed)} km/h
          </span>
        </div>

        {/* 평균 심박수 */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <FaHeartbeat className="text-red-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">
              평균 심박수
            </span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {fmt(wr.avgHeartrate)} bpm
          </span>
        </div>

        {/* 최대 심박수 */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <FaHeartbeat className="text-red-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">
              최대 심박수
            </span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {fmt(wr.maxHeartrate)} bpm
          </span>
        </div>

        {/* 평균 페이스 */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
              <FaRunning className="text-purple-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">
              평균 페이스
            </span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {fmt(wr.avgPace)} 분/km
          </span>
        </div>

        {/* 케이던스 */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <FaRunning className="text-green-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">
              케이던스
            </span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {fmt(wr.cadence)} spm
          </span>
        </div>
      </div>
    </div>
  );
}

export default WorkRecordDetailView;
